import { FC } from 'react';
import { HStack, VStack, Input, NativeSelect, Button, Field } from '@chakra-ui/react';
import { LuX } from 'react-icons/lu';
import { AIAnalysisHistoryFilters } from '@/types/aiManagement';
import { Dog } from '@/types/dog';

interface HistoryFiltersProps {
  filters: AIAnalysisHistoryFilters;
  onFilterChange: (filters: AIAnalysisHistoryFilters) => void;
  femaleDogs: Dog[];
}

/**
 * 分析履歴のフィルターコンポーネント
 */
export const HistoryFilters: FC<HistoryFiltersProps> = ({
  filters,
  onFilterChange,
  femaleDogs,
}) => {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFilterChange({
      ...filters,
      startDate: value ? new Date(value) : undefined,
    });
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onFilterChange({
      ...filters,
      endDate: value ? new Date(value) : undefined,
    });
  };

  const handleFemaleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFilterChange({
      ...filters,
      femaleId: value || undefined,
    });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as 'success' | 'error' | '';
    onFilterChange({
      ...filters,
      status: value || undefined,
    });
  };

  const handleReset = () => {
    onFilterChange({});
  };

  const hasFilters =
    filters.startDate || filters.endDate || filters.femaleId || filters.status;

  // 日付をinput用のフォーマットに変換
  const formatDateForInput = (date?: Date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  return (
    <VStack align="stretch" gap={4} bg="white" p={4} borderRadius="lg" shadow="sm">
      <HStack gap={4} flexWrap="wrap">
        {/* 期間フィルター */}
        <Field.Root flex={1} minW="140px">
          <Field.Label fontSize="sm">開始日</Field.Label>
          <Input
            type="date"
            size="sm"
            value={formatDateForInput(filters.startDate)}
            onChange={handleStartDateChange}
          />
        </Field.Root>

        <Field.Root flex={1} minW="140px">
          <Field.Label fontSize="sm">終了日</Field.Label>
          <Input
            type="date"
            size="sm"
            value={formatDateForInput(filters.endDate)}
            onChange={handleEndDateChange}
          />
        </Field.Root>

        {/* メス犬フィルター */}
        <Field.Root flex={1} minW="150px">
          <Field.Label fontSize="sm">メス犬</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={filters.femaleId || ''}
              onChange={handleFemaleChange}
            >
              <option value="">すべて</option>
              {femaleDogs.map((dog) => (
                <option key={dog.id} value={dog.id}>
                  {dog.name}
                </option>
              ))}
            </NativeSelect.Field>
          </NativeSelect.Root>
        </Field.Root>

        {/* ステータスフィルター */}
        <Field.Root flex={1} minW="120px">
          <Field.Label fontSize="sm">ステータス</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={filters.status || ''}
              onChange={handleStatusChange}
            >
              <option value="">すべて</option>
              <option value="success">成功</option>
              <option value="error">エラー</option>
            </NativeSelect.Field>
          </NativeSelect.Root>
        </Field.Root>
      </HStack>

      {/* リセットボタン */}
      {hasFilters && (
        <HStack justify="flex-end">
          <Button size="sm" variant="ghost" onClick={handleReset}>
            <LuX />
            フィルターをクリア
          </Button>
        </HStack>
      )}
    </VStack>
  );
};
